import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaUserTie,
  FaGlobeAmericas,
  FaFileAlt,
  FaPassport,
  FaUniversity,
  FaShieldAlt,
} from "react-icons/fa";
import "../Styles/ProcessStory.css";

/**
 * The six stages, told as a story rather than a feature list.
 *
 * Each stage names who does what, because "will they actually do this for me
 * or just hand me a checklist?" is the question every applicant is really
 * asking and almost no consultancy answers.
 */
const STAGES = [
  {
    n: "01",
    icon: <FaUserTie />,
    when: "Week 1",
    title: "We find out what you actually want",
    lede: "Before any country is mentioned.",
    body: "One sitting, an hour, in our Andheri West office or on video. We go through your marks, your budget, your family situation and what you want your life to look like in five years. No sales pitch — we have turned people away when going abroad was the wrong call.",
    you: "Bring your marksheets and a rough budget.",
    us: "An honest read on what your profile can reach.",
  },
  {
    n: "02",
    icon: <FaGlobeAmericas />,
    when: "Week 1–2",
    title: "We shortlist countries, not brochures",
    lede: "Built around your score and budget.",
    body: "You get a written shortlist of universities or immigration streams, each with real numbers attached: tuition, living costs, intake deadlines, post-study work rights and the odds of approval. Ordered by what fits you — never by which institution pays the largest commission.",
    you: "Tell us which ones excite you and which ones do not.",
    us: "A ranked shortlist with the trade-offs spelled out.",
  },
  {
    n: "03",
    icon: <FaFileAlt />,
    when: "Week 2–6",
    title: "We build the file with you",
    lede: "The part that decides everything.",
    body: "Transcripts, credential evaluation, financial proof, and the Statement of Purpose. We draft the SOP across several sittings with you in the room. Admissions officers spot a template instantly — so we write your actual story, in your actual voice, and then we cut it until it is sharp.",
    you: "Several honest conversations, and your documents on time.",
    us: "Drafting, auditing and rewriting until the file holds up.",
  },
  {
    n: "04",
    icon: <FaUniversity />,
    when: "Runs alongside",
    title: "We sort out the money",
    lede: "Before it becomes a crisis.",
    body: "Direct lines to verified loan partners — Avanse, HDFC Credila, Axis — plus every merit scholarship your profile qualifies for, mapped and deadlined. We tell you the full landed cost in writing at the start, so the number never moves on you later.",
    you: "Your family's financial documents.",
    us: "Loan introductions, scholarship mapping, a written cost sheet.",
  },
  {
    n: "05",
    icon: <FaPassport />,
    when: "Filing window",
    title: "We file it, and we track it",
    lede: "Zero margin for error.",
    body: "Applications go in through authorised university and immigration portals, with every deadline tracked on our side, not yours. You get told what has been submitted and what comes next — you should never have to chase us for a status update.",
    you: "Approve, sign, and keep your phone on.",
    us: "Submission, deadline tracking, and every follow-up with the embassy.",
  },
  {
    n: "06",
    icon: <FaShieldAlt />,
    when: "After approval",
    title: "We stay on the phone after you land",
    lede: "The part most agencies skip.",
    body: "Mock visa interviews until you stop being nervous. Then forex, insurance, student airfares, accommodation and a pre-departure briefing. And after you land: the same Mumbai number, answered by the same person who counselled you. Some of our students still call us years later.",
    you: "Land, settle in, and call when you need to.",
    us: "Interview rehearsal, departure logistics, and a number that answers.",
  },
];

export default function ProcessStory() {
  const [active, setActive] = useState(0);
  const stageRefs = useRef([]);

  useEffect(() => {
    // Whichever stage is nearest the middle of the viewport is the one being read.
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const idx = stageRefs.current.indexOf(entry.target);
          if (idx !== -1) setActive(idx);
        });
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    );

    stageRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, []);

  const progress = ((active + 1) / STAGES.length) * 100;

  return (
    <section className="ve-section ve-process" id="process">
      <div className="ve-container">
        <div className="ve-section-head">
          <span className="ve-eyebrow">The process, start to finish</span>
          <h2 className="ve-h2">
            Nobody tells you what the next{" "}
            <span className="ve-serif">eighteen months</span> look like. We will.
          </h2>
          <p className="ve-lede">
            Six stages. One advisor who stays with you through all of them. Here is exactly
            what happens, when it happens, and which parts are ours to carry.
          </p>
        </div>

        <div className="ve-process__layout">
          {/* ---- Sticky rail ---- */}
          <aside className="ve-process__rail" aria-hidden="true">
            <div className="ve-process__rail-inner">
              <div className="ve-process__counter">
                <span className="ve-process__counter-now">{STAGES[active].n}</span>
                <span className="ve-process__counter-of">/ {STAGES.length}</span>
              </div>

              <div className="ve-process__track">
                {/* Expose progress as a custom property, not a transform — the
                    rail scales vertically on desktop and horizontally on mobile. */}
                <span
                  className="ve-process__track-fill"
                  style={{ "--ve-progress": progress / 100 }}
                />
              </div>

              <ol className="ve-process__toc">
                {STAGES.map((s, i) => (
                  <li
                    key={s.n}
                    className={i === active ? "is-active" : i < active ? "is-done" : ""}
                  >
                    <span className="ve-process__toc-dot" />
                    {s.title}
                  </li>
                ))}
              </ol>
            </div>
          </aside>

          {/* ---- Stages ---- */}
          <ol className="ve-process__stages">
            {STAGES.map((s, i) => (
              <li
                key={s.n}
                ref={(el) => (stageRefs.current[i] = el)}
                className={`ve-stage ${i === active ? "is-active" : ""}`}
              >
                <div className="ve-stage__head">
                  <span className="ve-stage__n">{s.n}</span>
                  <span className="ve-stage__icon">{s.icon}</span>
                  <span className="ve-stage__when">{s.when}</span>
                </div>

                <h3 className="ve-h3 ve-stage__title">{s.title}</h3>
                <p className="ve-stage__lede">{s.lede}</p>
                <p className="ve-stage__body">{s.body}</p>

                <dl className="ve-stage__split">
                  <div>
                    <dt>Your part</dt>
                    <dd>{s.you}</dd>
                  </div>
                  <div>
                    <dt>Our part</dt>
                    <dd>{s.us}</dd>
                  </div>
                </dl>
              </li>
            ))}
          </ol>
        </div>

        <div className="ve-process__cta">
          <div>
            <h3 className="ve-h3">Stage one is free, and it is genuinely no-obligation.</h3>
            <p>An hour with a senior counsellor. You leave knowing where you stand.</p>
          </div>
          <Link to="/consultationForm" className="ve-btn ve-btn--primary">
            Book your first sitting <FaArrowRight />
          </Link>
        </div>
      </div>
    </section>
  );
}
